import React, { useEffect, useState, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { useAuth } from '../../../context/AuthContext.tsx';
import { isUserSuperAdmin } from '../../../utils/userTier.ts';
import { 
    fetchRamasProfesionales, 
    createRamaProfesional, 
    updateRamaProfesional, 
    deleteRamaProfesional, 
    agregarSubrama, 
    eliminarSubrama, 
    RamaProfesional 
} from '../../../services/ramaProfesionalService.ts';
import { 
    ShieldAlert, 
    Plus, 
    Edit2, 
    Trash2, 
    RefreshCw, 
    Check, 
    X, 
    Tag, 
    Sparkles, 
    Layers, 
    ArrowLeft 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RamasProfesionalesPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isSuperAdmin = isUserSuperAdmin(user);

    const [ramas, setRamas] = useState<RamaProfesional[]>([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    // Estado para nueva sub-rama por rama id
    const [newSubramaText, setNewSubramaText] = useState<{ [key: number]: string }>({});
    const [submittingSubrama, setSubmittingSubrama] = useState<{ [key: number]: boolean }>({});

    // Modal de edición o creación de rama
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRama, setEditingRama] = useState<RamaProfesional | null>(null);
    const [formData, setFormData] = useState({
        nombre: '',
        codigo: '',
        icono: '🏗️',
        color: '#61257B',
        color_dark: '#C1348B',
        orden: 1,
    });
    const [savingRama, setSavingRama] = useState(false);

    const loadRamas = useCallback(async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const data = await fetchRamasProfesionales();
            setRamas(data);
        } catch (err: any) {
            console.error('Error cargando ramas:', err);
            setErrorMsg('No se pudieron cargar las ramas profesionales.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isSuperAdmin) {
            loadRamas();
        }
    }, [isSuperAdmin, loadRamas]);

    const showToast = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(null), 3500);
    };

    const handleOpenCreateModal = () => {
        setEditingRama(null);
        setFormData({
            nombre: '',
            codigo: '',
            icono: '💼',
            color: '#61257B',
            color_dark: '#C1348B',
            orden: ramas.length + 1,
        });
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (rama: RamaProfesional) => {
        setEditingRama(rama);
        setFormData({
            nombre: rama.nombre,
            codigo: rama.codigo,
            icono: rama.icono || '💼',
            color: rama.color || '#61257B',
            color_dark: rama.color_dark || '#C1348B',
            orden: rama.orden || 1,
        });
        setIsModalOpen(true);
    };

    const handleSaveRama = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingRama(true);
        try {
            if (editingRama) {
                const updated = await updateRamaProfesional(editingRama.id, formData);
                setRamas(prev => prev.map(r => r.id === updated.id ? updated : r));
                showToast(`Rama "${updated.nombre}" actualizada con éxito.`);
            } else {
                const created = await createRamaProfesional({
                    ...formData,
                    subramas: [],
                    activo: true,
                });
                setRamas(prev => [...prev, created]);
                showToast(`Rama "${created.nombre}" creada con éxito.`);
            }
            setIsModalOpen(false);
        } catch (err: any) {
            console.error('Error guardando rama:', err);
            setErrorMsg('Hubo un error al guardar la rama.');
        } finally {
            setSavingRama(false);
        }
    };

    const handleDeleteRama = async (rama: RamaProfesional) => {
        if (!window.confirm(`¿Estás seguro de eliminar la rama "${rama.nombre}"?`)) return;
        try {
            await deleteRamaProfesional(rama.id);
            setRamas(prev => prev.filter(r => r.id !== rama.id));
            showToast(`Rama "${rama.nombre}" eliminada.`);
        } catch (err: any) {
            console.error('Error eliminando rama:', err);
            setErrorMsg('No se pudo eliminar la rama.');
        }
    };

    const handleAddSubrama = async (ramaId: number) => {
        const text = (newSubramaText[ramaId] || '').trim();
        if (!text) return;
        setSubmittingSubrama(prev => ({ ...prev, [ramaId]: true }));
        try {
            const updated = await agregarSubrama(ramaId, text);
            setRamas(prev => prev.map(r => r.id === updated.id ? updated : r));
            setNewSubramaText(prev => ({ ...prev, [ramaId]: '' }));
            showToast(`Sub-rama "${text}" agregada correctamente.`);
        } catch (err: any) {
            console.error('Error agregando sub-rama:', err);
            setErrorMsg('No se pudo agregar la sub-rama.');
        } finally {
            setSubmittingSubrama(prev => ({ ...prev, [ramaId]: false }));
        }
    };

    const handleRemoveSubrama = async (ramaId: number, subramaName: string) => {
        try {
            const updated = await eliminarSubrama(ramaId, subramaName);
            setRamas(prev => prev.map(r => r.id === updated.id ? updated : r));
            showToast(`Sub-rama "${subramaName}" eliminada.`);
        } catch (err: any) {
            console.error('Error eliminando sub-rama:', err);
            setErrorMsg('No se pudo eliminar la sub-rama.');
        }
    };

    if (!isSuperAdmin) {
        return (
            <div style={{ padding: '60px 24px', maxWidth: '680px', margin: '0 auto', textAlign: 'center', fontFamily: "'DM Sans', sans-serif" }}>
                <div style={{
                    background: '#fff', borderRadius: '24px', padding: '48px 32px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.06)', border: '1.5px solid #fed7aa',
                }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: '#ea580c' }}>
                        <ShieldAlert size={32} />
                    </div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#0f172a', marginBottom: '8px' }}>
                        Acceso Restringido a Super Administradores
                    </h2>
                    <p style={{ fontSize: '0.92rem', color: '#64748b', lineHeight: '1.6', marginBottom: '24px' }}>
                        Solo el personal con rol de <strong>Super Admin</strong> puede configurar las ramas principales y sub-ramas profesionales del ecosistema Co Marca.
                    </p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        style={{
                            background: '#61257B', color: '#fff', border: 'none', borderRadius: '12px',
                            padding: '12px 28px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer',
                        }}
                    >
                        Volver al Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", padding: '24px 32px 80px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Cabecera de Página */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '28px' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#64748b', marginBottom: '6px' }}>
                        <span onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} className="hover:text-[#61257B] transition-colors">Dashboard</span>
                        <span>/</span>
                        <span>Landing</span>
                        <span>/</span>
                        <span style={{ color: '#61257B', fontWeight: '700' }}>Ramas Profesionales</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <h1 style={{ fontSize: '1.75rem', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.02em', margin: 0 }}>
                            Ramas & Especialidades del Banco
                        </h1>
                        <span style={{
                            background: 'rgba(97, 37, 123, 0.1)', color: '#61257B',
                            border: '1.5px solid rgba(97, 37, 123, 0.25)', borderRadius: '100px',
                            padding: '4px 12px', fontSize: '0.74rem', fontWeight: '800',
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                        }}>
                            <Sparkles size={12} /> Solo SuperAdmin
                        </span>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '6px', maxWidth: '750px', lineHeight: '1.5' }}>
                        Configura las ramas principales del Banco de Profesionales y define las sub-ramas específicas que verán los usuarios al registrar su perfil.
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                        onClick={loadRamas}
                        title="Recargar ramas"
                        style={{
                            width: '42px', height: '42px', borderRadius: '12px',
                            background: '#fff', border: '1.5px solid #e2e8f0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: '#475569', transition: 'all 0.2s',
                        }}
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button
                        onClick={handleOpenCreateModal}
                        style={{
                            background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                            color: '#fff', border: 'none', borderRadius: '12px',
                            padding: '10px 20px', fontSize: '0.88rem', fontWeight: '700',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                            boxShadow: '0 4px 14px rgba(97, 37, 123, 0.3)',
                        }}
                    >
                        <Plus size={18} />
                        Nueva Rama
                    </button>
                </div>
            </div>

            {/* Toasts / Notificaciones */}
            {successMsg && (
                <div style={{
                    background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#166534',
                    borderRadius: '12px', padding: '12px 18px', marginBottom: '20px',
                    display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', fontWeight: '600',
                }}>
                    <Check size={18} color="#16a34a" /> {successMsg}
                </div>
            )}
            {errorMsg && (
                <div style={{
                    background: '#fef2f2', border: '1.5px solid #fecaca', color: '#991b1b',
                    borderRadius: '12px', padding: '12px 18px', marginBottom: '20px',
                    display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', fontWeight: '600',
                }}>
                    <X size={18} color="#dc2626" /> {errorMsg}
                </div>
            )}

            {/* Grid de Ramas Profesionales */}
            {loading && ramas.length === 0 ? (
                <Loading text="Cargando ramas profesionales..." />
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '22px' }}>
                    {ramas.map(rama => {
                        const subramas = Array.isArray(rama.subramas) ? rama.subramas : [];
                        const isSubmitting = !!submittingSubrama[rama.id];

                        return (
                            <div
                                key={rama.id}
                                style={{
                                    background: '#fff',
                                    borderRadius: '20px',
                                    border: '1.5px solid #f1f5f9',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                }}
                            >
                                {/* Header de la Rama */}
                                <div style={{
                                    padding: '18px 22px',
                                    background: `linear-gradient(135deg, ${rama.color}15 0%, ${rama.color_dark || rama.color}08 100%)`,
                                    borderBottom: `2px solid ${rama.color}25`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{
                                            width: '44px', height: '44px', borderRadius: '12px',
                                            background: `linear-gradient(135deg, ${rama.color}, ${rama.color_dark || rama.color})`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '1.4rem', color: '#fff', boxShadow: `0 4px 12px ${rama.color}35`,
                                        }}>
                                            {rama.icono}
                                        </div>
                                        <div>
                                            <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                                                {rama.nombre}
                                            </h3>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px' }}>
                                                <span style={{ fontSize: '0.72rem', color: rama.color, fontWeight: '700', background: `${rama.color}18`, padding: '1px 8px', borderRadius: '6px' }}>
                                                    {rama.codigo}
                                                </span>
                                                <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                                    {subramas.length} sub-ramas
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <button
                                            onClick={() => handleOpenEditModal(rama)}
                                            title="Editar rama"
                                            style={{
                                                width: '32px', height: '32px', borderRadius: '8px',
                                                background: '#fff', border: '1px solid #e2e8f0',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer', color: '#334155',
                                            }}
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteRama(rama)}
                                            title="Eliminar rama"
                                            style={{
                                                width: '32px', height: '32px', borderRadius: '8px',
                                                background: '#fff', border: '1px solid #fee2e2',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer', color: '#ef4444',
                                            }}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>

                                {/* Cuerpo: Lista de Sub-ramas */}
                                <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', flex: 1, gap: '14px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            Especialidades / Sub-ramas
                                        </span>
                                        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                            Visibles para el usuario
                                        </span>
                                    </div>

                                    {/* Chips de subramas */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', minHeight: '60px' }}>
                                        {subramas.length === 0 ? (
                                            <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic', padding: '10px 0' }}>
                                                Sin sub-ramas configuradas. Agrega la primera abajo.
                                            </div>
                                        ) : (
                                            subramas.map((sub, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                        background: `${rama.color}10`, border: `1.5px solid ${rama.color}25`,
                                                        borderRadius: '100px', padding: '4px 12px',
                                                        fontSize: '0.78rem', fontWeight: '700', color: '#1e293b',
                                                        transition: 'all 0.18s ease',
                                                    }}
                                                >
                                                    <span>{sub}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveSubrama(rama.id, sub)}
                                                        title={`Eliminar ${sub}`}
                                                        style={{
                                                            border: 'none', background: 'transparent',
                                                            cursor: 'pointer', padding: 0,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            color: '#94a3b8',
                                                        }}
                                                        onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
                                                        onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
                                                    >
                                                        <X size={13} />
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {/* Agregar nueva subrama inline */}
                                    <div style={{ marginTop: 'auto', paddingTop: '14px', borderTop: '1px solid #f1f5f9' }}>
                                        <form
                                            onSubmit={e => { e.preventDefault(); handleAddSubrama(rama.id); }}
                                            style={{ display: 'flex', gap: '8px' }}
                                        >
                                            <input
                                                type="text"
                                                value={newSubramaText[rama.id] || ''}
                                                onChange={e => setNewSubramaText(prev => ({ ...prev, [rama.id]: e.target.value }))}
                                                placeholder="Ej: Ingeniero Sanitario, Auditor..."
                                                style={{
                                                    flex: 1, padding: '8px 12px', borderRadius: '10px',
                                                    border: '1.5px solid #e2e8f0', fontSize: '0.82rem',
                                                    outline: 'none', fontFamily: "'DM Sans', sans-serif",
                                                }}
                                            />
                                            <button
                                                type="submit"
                                                disabled={isSubmitting || !(newSubramaText[rama.id] || '').trim()}
                                                style={{
                                                    background: (newSubramaText[rama.id] || '').trim() ? rama.color : '#f1f5f9',
                                                    color: (newSubramaText[rama.id] || '').trim() ? '#fff' : '#94a3b8',
                                                    border: 'none', borderRadius: '10px',
                                                    padding: '8px 14px', fontSize: '0.8rem', fontWeight: '700',
                                                    cursor: (newSubramaText[rama.id] || '').trim() ? 'pointer' : 'not-allowed',
                                                    display: 'flex', alignItems: 'center', gap: '4px',
                                                }}
                                            >
                                                <Plus size={14} />
                                                Agregar
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal de Crear / Editar Rama */}
            {isModalOpen && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '24px', maxWidth: '500px', width: '100%',
                        boxShadow: '0 25px 60px rgba(0,0,0,0.25)', overflow: 'hidden', border: '1.5px solid #f1f5f9',
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                            padding: '24px 28px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <div>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', margin: 0 }}>
                                    {editingRama ? 'Editar Rama Profesional' : 'Nueva Rama Profesional'}
                                </h3>
                                <div style={{ fontSize: '0.78rem', opacity: 0.9, marginTop: '2px' }}>
                                    Categoría principal para el Banco de Profesionales
                                </div>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
                                    width: '32px', height: '32px', color: '#fff', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSaveRama} style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                    Nombre de la Rama *
                                </label>
                                <input
                                    type="text" required
                                    value={formData.nombre}
                                    onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                    placeholder="Ej: Ingeniería & Arquitectura"
                                    style={{
                                        width: '100%', padding: '10px 14px', borderRadius: '10px',
                                        border: '1.5px solid #e2e8f0', fontSize: '0.9rem', outline: 'none',
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '14px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Código (ID único) *
                                    </label>
                                    <input
                                        type="text" required
                                        value={formData.codigo}
                                        onChange={e => setFormData(prev => ({ ...prev, codigo: e.target.value.toLowerCase().trim() }))}
                                        placeholder="ej: ingenieria"
                                        disabled={!!editingRama}
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: '1.5px solid #e2e8f0', fontSize: '0.9rem', outline: 'none',
                                            background: editingRama ? '#f8fafc' : '#fff',
                                        }}
                                    />
                                </div>
                                <div style={{ width: '90px' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Icono
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.icono}
                                        onChange={e => setFormData(prev => ({ ...prev, icono: e.target.value }))}
                                        placeholder="🏗️"
                                        style={{
                                            width: '100%', padding: '10px 14px', borderRadius: '10px',
                                            border: '1.5px solid #e2e8f0', fontSize: '1.2rem', textAlign: 'center', outline: 'none',
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '14px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Color Principal
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="color"
                                            value={formData.color}
                                            onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                                            style={{ width: '40px', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }}
                                        />
                                        <input
                                            type="text"
                                            value={formData.color}
                                            onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                                            style={{ flex: 1, padding: '9px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.85rem' }}
                                        />
                                    </div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                                        Color Secundario
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="color"
                                            value={formData.color_dark}
                                            onChange={e => setFormData(prev => ({ ...prev, color_dark: e.target.value }))}
                                            style={{ width: '40px', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }}
                                        />
                                        <input
                                            type="text"
                                            value={formData.color_dark}
                                            onChange={e => setFormData(prev => ({ ...prev, color_dark: e.target.value }))}
                                            style={{ flex: 1, padding: '9px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.85rem' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    style={{
                                        padding: '10px 18px', borderRadius: '10px', border: '1.5px solid #e2e8f0',
                                        background: '#f8fafc', color: '#64748b', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingRama}
                                    style={{
                                        background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                                        color: '#fff', border: 'none', borderRadius: '10px',
                                        padding: '10px 22px', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
                                    }}
                                >
                                    {savingRama ? 'Guardando...' : (editingRama ? 'Guardar Cambios' : 'Crear Rama')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RamasProfesionalesPage;
